import { FormEvent, useEffect, useMemo, useState } from "react";
import type { WorkObjectFieldDefinition } from "../../../../entities/work-object-field/model/use-work-object-fields";
import {
  buildFieldValueInput,
  createFieldValueDrafts,
  fieldDefinitionsForType,
  type WorkObjectFieldValueDraft,
} from "../../../../entities/work-object/model/work-object-field-drafts";
import type { CreateWorkObjectInput, WorkObjectListView } from "../../../../entities/work-object/model/use-work-objects";

type UseCreateWorkObjectFormOptions = {
  // Какой тип предложить первым. Раньше это всегда был первый в списке —
  // то есть Epic, — и каждая мелкая задача начиналась со смены типа.
  defaultTypeId?: string | null;
  fieldDefinitions: WorkObjectFieldDefinition[];
  listView: WorkObjectListView;
  onCreate: (input: CreateWorkObjectInput) => Promise<void>;
  // Под каким ключом помнить последний выбранный тип (обычно id доски): на
  // доске багов человек заводит баги, и форма не должна каждый раз спорить.
  rememberKey?: string | null;
};

const rememberedTypePrefix = "impacto.createType.";

function readRememberedType(rememberKey: string | null | undefined): string | null {
  if (!rememberKey) return null;
  try {
    return localStorage.getItem(`${rememberedTypePrefix}${rememberKey}`);
  } catch {
    return null;
  }
}

function writeRememberedType(rememberKey: string | null | undefined, typeId: string): void {
  if (!rememberKey || !typeId) return;
  try {
    localStorage.setItem(`${rememberedTypePrefix}${rememberKey}`, typeId);
  } catch {
    // Хранилище может быть недоступно (приватное окно) — форма от этого не ломается.
  }
}

export function useCreateWorkObjectForm({
  defaultTypeId: preferredTypeId,
  fieldDefinitions,
  listView,
  onCreate,
  rememberKey,
}: UseCreateWorkObjectFormOptions) {
  // Порядок: что человек выбирал здесь в прошлый раз → что предлагает контекст
  // (самый частый тип на доске) → первый попавшийся. Каждый кандидат должен
  // существовать: тип могли заархивировать с тех пор.
  const defaultTypeId = useMemo(() => {
    const known = new Set(listView.types.map((type) => type.id));
    const candidates = [readRememberedType(rememberKey), preferredTypeId, listView.types[0]?.id];
    return candidates.find((candidate): candidate is string => Boolean(candidate && known.has(candidate))) ?? "";
  }, [listView.types, preferredTypeId, rememberKey]);
  const [title, setTitle] = useState("");
  const [typeId, setTypeIdState] = useState(defaultTypeId);
  const setTypeId = (next: string) => {
    setTypeIdState(next);
    writeRememberedType(rememberKey, next);
  };
  const [fieldValueDrafts, setFieldValueDrafts] = useState<Record<string, WorkObjectFieldValueDraft>>({});
  const [error, setError] = useState<string | null>(null);
  const applicableFieldDefinitions = useMemo(
    () => fieldDefinitionsForType(fieldDefinitions, typeId),
    [fieldDefinitions, typeId],
  );

  useEffect(() => {
    setTypeIdState((current) => current || defaultTypeId);
  }, [defaultTypeId]);

  useEffect(() => {
    setFieldValueDrafts(createFieldValueDrafts(applicableFieldDefinitions));
  }, [applicableFieldDefinitions]);

  const canSubmit = title.trim().length >= 2 && Boolean(typeId);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }

    setError(null);
    try {
      await onCreate({
        title,
        typeId,
        values: buildFieldValueInput(applicableFieldDefinitions, fieldValueDrafts),
      });
      setTitle("");
      setFieldValueDrafts(createFieldValueDrafts(applicableFieldDefinitions));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create item.");
    }
  }

  return {
    canSubmit,
    error,
    fieldDefinitions: applicableFieldDefinitions,
    fieldValueDrafts,
    setFieldValueDrafts,
    setTitle,
    setTypeId,
    submit,
    title,
    typeId,
  };
}
